import { Entity } from '@brochington/ecstatic';
import ECSMapContext from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MarkupKitContext from 'contexts/MarkupKitContext';
import { Components } from 'editor/components';
import MapboxCanvasSource from 'editor/components/map/MapboxCanvasSource';
import Transform from 'editor/components/Transform';
import Immutable, { OrderedMap } from 'immutable';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MappedSheetVersion } from 'records/MappedSheetVersion';
import { Markup as MarkupRecord } from 'records/Markup';
import { SheetVersion } from 'records/SheetVersion';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ECSMapSheetMarkup from './ECSMapSheetMarkup';

interface Props extends WithAppStoreProps {
  mappedSheetVersion: MappedSheetVersion;
}

const ECSMapSheetMarkups: FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
) => {
  const { mappedSheetVersion, appState, actions } = props;
  const { markups } = appState;
  const {
    map: mapComp,
    markupsOverlay,
    markupsOverlayCanvas,
  } = useContext(ECSMapContext);
  const world = useContext(ECSWorldContext);
  const markupKit = useContext(MarkupKitContext);
  const [markupsLayer, setMarkupsLayer] = useState<Entity<Components> | null>(
    null
  );
  const [transformComp, setTransformComp] = useState<Transform>();
  const [markupsToRender, setMarkupsToRender] = useState<
    OrderedMap<string, MarkupRecord>
  >(OrderedMap());

  const sheet = appState.sheets.find((s) =>
    s.versions.some((sv) => sv.sheetid === mappedSheetVersion.sheetid)
  );

  const pageIds = sheet?.versions.map((version) => version.pageid);

  if (!sheet) return null;

  const { pageid, width, height } = sheet.versions.last<SheetVersion>();

  async function fetchAllMarkups(): Promise<void> {
    if (sheet) {
      for (const version of sheet.versions) {
        await actions.getSheetMarkups(version.pageid);
      }
    }
  }

  useEffect(() => {
    let sheetMarkups = Immutable.OrderedMap<string, MarkupRecord>();
    pageIds?.forEach((pageid) => {
      const pageMarkup = markups.get(
        pageid,
        Immutable.OrderedMap<string, MarkupRecord>()
      );
      if (pageMarkup.size > 0) {
        sheetMarkups = sheetMarkups.mergeDeep(pageMarkup);
      }
    });
    setMarkupsToRender(sheetMarkups);
  }, [markups]);

  useEffect(() => {
    const markupsLayerTag = `markupsLayer-${pageid}`;

    async function createMarkupLayers(): Promise<void> {
      if (markupKit) {
        fetchAllMarkups();

        const { imagesource } = mappedSheetVersion;
        if (!imagesource) return;

        const {
          calibrationconfig: { corners },
        } = imagesource;

        // The Canvas Source will probably go away, as no source/layer is needed when markups are drawn to an overlay.
        // Keeping around for now so I can think about a better way to handle this case.
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const markupsSource: mapboxgl.CanvasSourceRaw = {
          type: 'canvas',
          canvas,
          coordinates: [
            [corners.topleft.longitude, corners.topleft.latitude],
            [corners.topright.longitude, corners.topright.latitude],
            [corners.bottomright.longitude, corners.bottomright.latitude],
            [corners.bottomleft.longitude, corners.bottomleft.latitude],
          ],
          animate: true,
        };

        const markupsSourceComp = new MapboxCanvasSource(markupsSource);

        // NOTE: This transform is shared with both the entity that contains the markupsOverlay,
        //       and each markup entity so that the transform can be set once per overlay.
        const transform = new Transform();
        setTransformComp(transform);

        const _markupsLayer = world
          .createEntity()
          .add(mapComp)
          .add(markupsOverlay)
          .add(markupsOverlayCanvas)
          .add(markupsSourceComp)
          .add(transform)
          .addTag('markupsLayer')
          .addTag(markupsLayerTag);

        setMarkupsLayer(_markupsLayer);
      }
    }
    createMarkupLayers();

    return () => {
      if (mapComp.map) {
        const _markupsLayer = world.getTagged(markupsLayerTag);
        if (!_markupsLayer) return;

        const _sourceComp = _markupsLayer.get(MapboxCanvasSource);

        // Mapbox throws an error if the source is not present when
        // using map.isSourceLoaded. Wrapping in try/catch here
        // to prevent rest of app from breaking.
        try {
          if (mapComp.map.isSourceLoaded(_sourceComp.id)) {
            mapComp.map.removeSource(_sourceComp.id);
          }
        } catch (err) {
          console.error(
            `Source ${_sourceComp.id} does not exist on map instance: ${err}`
          );
        }

        _markupsLayer.destroy();
      }
    };
  }, []);

  // Update markups layer
  useEffect(() => {
    async function updateMarkupLayers(): Promise<void> {
      if (markupsLayer) {
        const { imagesource } = mappedSheetVersion;

        if (!imagesource) {
          return;
        }

        const { corners } = imagesource.calibrationconfig;
        const sourceComp = markupsLayer.get(MapboxCanvasSource);

        // NOTE: Right now you have to set tracked.source directly in order
        //       for it to be picked up by Tracked. Need to look into other options.
        sourceComp.tracked.source = {
          ...sourceComp.tracked.source,
          // @ts-ignore // TODO: better types via the component would be grand.
          coordinates: [
            [corners.topleft.longitude, corners.topleft.latitude],
            [corners.topright.longitude, corners.topright.latitude],
            [corners.bottomright.longitude, corners.bottomright.latitude],
            [corners.bottomleft.longitude, corners.bottomleft.latitude],
          ],
        };
      }
    }

    updateMarkupLayers();
  }, [markupsLayer, mappedSheetVersion]);

  if (markupsLayer && transformComp) {
    return (
      <>
        {markupsToRender
          .valueSeq()
          .map((markup) => {
            return (
              <ECSMapSheetMarkup
                key={markup.markupid}
                markup={markup}
                transform={transformComp}
              />
            );
          })
          .toList()}
      </>
    );
  }

  return null;
};

export default withAppStore<Props>(ECSMapSheetMarkups);
